import type { SchedulePrefs, LateNightPref } from '../types';

/**
 * Clock handling and migration for the scheduler's planning preferences.
 *
 * The wizard used to ask a single "when are you at your best?" 1–5 question,
 * which the planner then had to guess actual hours from. It now asks for a
 * wake time, a bed time and how often late nights are welcome — the same
 * intent, but in units the schedule can be built against directly.
 */

/** Slider bounds, in minutes from midnight. 30-minute steps throughout. */
export const WAKE_MIN = 4 * 60;    // 04:00
export const WAKE_MAX = 12 * 60;   // 12:00
export const STEP_MIN = 30;

/**
 * Bed time is held as minutes from 18:00 so the range can run past midnight
 * without the slider wrapping backwards: 0 = 18:00 … 540 = 03:00.
 */
export const BED_ORIGIN = 18 * 60;
export const BED_SPAN_MAX = 9 * 60; // 18:00 + 9h = 03:00

export function minutesToTime(mins: number): string {
  const m = ((Math.round(mins / STEP_MIN) * STEP_MIN) % (24 * 60) + 24 * 60) % (24 * 60);
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

export function timeToMinutes(time: string): number {
  const [h, m] = (time || '').split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

/** Minutes-from-18:00 for a bed time, treating small-hours times as "tonight". */
export function bedTimeToSpan(time: string): number {
  const mins = timeToMinutes(time);
  const span = mins < BED_ORIGIN ? mins + 24 * 60 - BED_ORIGIN : mins - BED_ORIGIN;
  return Math.min(BED_SPAN_MAX, Math.max(0, span));
}

export function spanToBedTime(span: number): string {
  return minutesToTime(BED_ORIGIN + span);
}

/** Local stored clock or datetime → a consistent 12-hour display time. */
export function formatClock(time: string): string {
  const clock = time.includes('T') ? time.split('T')[1] : time;
  if (!/^\d{1,2}:\d{2}(?::\d{2})?$/.test(clock)) return '';
  const mins = timeToMinutes(clock);
  if (mins < 0 || mins >= 24 * 60 || Number(clock.split(':')[1]) >= 60) return '';
  const h24 = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  const suffix = h24 < 12 ? 'am' : 'pm';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/**
 * Hours a day can be scheduled across, given a wake and bed time. A bed time
 * past midnight belongs to the following morning, so it has to be unwrapped
 * before subtracting or an 08:00–01:00 day comes out as negative.
 */
export function wakingHours(wakeTime: string, bedTime: string): number {
  const wake = timeToMinutes(wakeTime);
  let bed = timeToMinutes(bedTime);
  if (bed <= wake) bed += 24 * 60;
  return Math.round(((bed - wake) / 60) * 10) / 10;
}

/**
 * What the old 1–5 `sleep` answer becomes. Only used for prefs saved before
 * the wizard changed; a value in the middle of each band rather than anything
 * cleverer, since the original question never captured more than that.
 */
const SLEEP_MIGRATION: Record<number, { wakeTime: string; bedTime: string; lateNights: LateNightPref }> = {
  1: { wakeTime: '06:00', bedTime: '21:30', lateNights: 'rarely' },
  2: { wakeTime: '07:00', bedTime: '22:30', lateNights: 'rarely' },
  3: { wakeTime: '08:00', bedTime: '23:00', lateNights: 'sometimes' },
  4: { wakeTime: '09:00', bedTime: '00:00', lateNights: 'sometimes' },
  5: { wakeTime: '10:00', bedTime: '01:00', lateNights: 'often' },
};

export const DEFAULT_SCHEDULE_PREFS: SchedulePrefs = {
  pace: 3,
  wakeTime: '08:00',
  bedTime: '23:00',
  lateNights: 'sometimes',
  interests: { sightseeing: 3, exploring: 3, arts: 3, food: 3, nightlife: 3 },
  walking: 'moderate',
  transport: 'fastest',
  budget: 2,
};

/**
 * Brings any saved prefs up to the current shape. Firestore hands back
 * whatever was written whenever it was written, so this has to cope with
 * prefs that predate the routine and transport questions entirely.
 */
export function migrateSchedulePrefs(raw: Partial<SchedulePrefs> | null | undefined): SchedulePrefs {
  const p = raw || {};
  const fromSleep = typeof p.sleep === 'number'
    ? SLEEP_MIGRATION[Math.min(5, Math.max(1, Math.round(p.sleep)))]
    : undefined;

  return {
    ...DEFAULT_SCHEDULE_PREFS,
    ...p,
    // Explicit answers win; otherwise derive from the old 1–5 question; only
    // then fall back to the default.
    wakeTime: p.wakeTime || fromSleep?.wakeTime || DEFAULT_SCHEDULE_PREFS.wakeTime,
    bedTime: p.bedTime || fromSleep?.bedTime || DEFAULT_SCHEDULE_PREFS.bedTime,
    lateNights: p.lateNights || fromSleep?.lateNights || DEFAULT_SCHEDULE_PREFS.lateNights,
    transport: p.transport || DEFAULT_SCHEDULE_PREFS.transport,
    interests: { ...DEFAULT_SCHEDULE_PREFS.interests, ...(p.interests || {}) },
  };
}
